import React from 'react';
import { Alert, Button, Divider, List, Space, Tag, Typography, message } from 'antd';
import { CheckCircleFilled, CopyOutlined, MailOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { signupPalette, signupGradient } from './signupTheme';
import type { SignupResult } from '../../../api/auth/signup';
import type { CashplusInitiateResponse } from '../../../api/paiement/paiement';

const { Text, Title } = Typography;

type Props = {
    result: SignupResult;
    cashplus?: CashplusInitiateResponse | null;
    currency?: string;
    /** Vrai quand une session a pu être ouverte (inscription via Google). */
    sessionReady: boolean;
    onEnterApp: () => void;
    onGoToLogin: () => void;
    /** Renseigné si le paiement a échoué après une création de comptes réussie. */
    paymentError?: string;
};

/**
 * Écran de fin de tunnel.
 *
 * Il annonce en une lecture ce qui a été créé et la seule action qui reste à
 * faire — définir son mot de passe, payer en agence, ou entrer dans l'app.
 */
const SignupDone: React.FC<Props> = ({
    result,
    cashplus,
    currency,
    sessionReady,
    onEnterApp,
    onGoToLogin,
    paymentError,
}) => {
    const { t } = useTranslation();

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
                <CheckCircleFilled style={{ fontSize: 56, color: signupPalette.secondary }} />
                <Title level={3} style={{ marginTop: 16, marginBottom: 4, color: signupPalette.textPrimary }}>
                    {t('signup.done.title', 'Vos comptes sont créés')}
                </Title>
                <Text style={{ color: signupPalette.textSecondary }}>
                    {t('signup.done.subtitle', 'Voici le récapitulatif de votre espace famille.')}
                </Text>
            </div>

            {/* Étape restante : définir le mot de passe */}
            {result.password_required &&
                (result.email_sent ? (
                    <Alert
                        type="info"
                        showIcon
                        icon={<MailOutlined />}
                        message={t('signup.done.checkEmailTitle', 'Vérifiez votre boîte mail')}
                        description={t(
                            'signup.done.checkEmail',
                            'Nous avons envoyé un lien à {{email}} pour définir votre mot de passe et activer votre compte.',
                            { email: result.parent.email }
                        )}
                    />
                ) : (
                    <Alert
                        type="warning"
                        showIcon
                        message={t('signup.done.emailFailedTitle', "L'email n'a pas pu être envoyé")}
                        description={t(
                            'signup.done.emailFailed',
                            'Vos comptes existent bien. Utilisez « Mot de passe oublié » depuis la page de connexion, ou contactez le support.'
                        )}
                    />
                ))}

            {/* Paiement CashPlus */}
            {cashplus && (
                <div
                    style={{
                        borderRadius: 16,
                        border: `2px solid ${signupPalette.primary}`,
                        overflow: 'hidden',
                    }}
                >
                    <div style={{ background: `${signupPalette.primary}0d`, padding: '16px 20px', textAlign: 'center' }}>
                        <img src="/images/Logo_CASHPLUS.png" alt="CashPlus" style={{ height: 30 }} />
                        <Text style={{ display: 'block', marginTop: 8, fontSize: 13, color: signupPalette.textSecondary }}>
                            {t(
                                'signup.done.cashplusInstructions',
                                'Présentez ce code dans n’importe quelle agence CashPlus pour finaliser votre paiement.'
                            )}
                        </Text>
                    </div>
                    <div style={{ padding: 20, textAlign: 'center' }}>
                        <Text
                            style={{
                                fontSize: 12,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                fontWeight: 600,
                                color: signupPalette.textSecondary,
                            }}
                        >
                            {t('signup.done.cashplusCode', 'Code de paiement')}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8 }}>
                            <Text style={{ fontSize: 26, fontWeight: 700, letterSpacing: 2, color: signupPalette.textPrimary }}>
                                {cashplus.cashplus_token}
                            </Text>
                            <Button
                                type="text"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard.writeText(cashplus.cashplus_token);
                                    message.success(t('signup.done.copied', 'Code copié !'));
                                }}
                                aria-label={t('signup.done.copy', 'Copier le code')}
                            />
                        </div>
                        <Divider style={{ margin: '16px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text style={{ color: signupPalette.textSecondary }}>
                                {t('signup.done.amount', 'Montant à payer')}
                            </Text>
                            <Text style={{ fontWeight: 700 }}>
                                {cashplus.total_amount.toFixed(2)} {currency || cashplus.currency || ''}
                            </Text>
                        </div>
                        {cashplus.cashplus_token_expiration && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                                <Text style={{ color: signupPalette.textSecondary }}>
                                    {t('signup.done.validUntil', 'Valable jusqu’au')}
                                </Text>
                                <Text>{new Date(cashplus.cashplus_token_expiration).toLocaleString()}</Text>
                            </div>
                        )}
                        <Text style={{ display: 'block', marginTop: 14, fontSize: 12, color: signupPalette.textSecondary }}>
                            {t(
                                'signup.done.activationNote',
                                'Les comptes seront activés automatiquement dès réception du paiement.'
                            )}
                        </Text>
                    </div>
                </div>
            )}

            {paymentError && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('signup.done.paymentFailedTitle', 'Comptes créés, paiement à finaliser')}
                    description={paymentError}
                />
            )}

            {/* Premium sans paiement lancé (« payer plus tard ») : on dit où aller. */}
            {result.plan === 'premium' && !cashplus && !paymentError && (
                <Alert
                    type="info"
                    showIcon
                    message={t('signup.done.payLaterTitle', 'Paiement à effectuer')}
                    description={t(
                        'signup.done.payLater',
                        'Vos comptes enfants seront actifs une fois le paiement enregistré. Connectez-vous puis rendez-vous sur la page « Paiement ».'
                    )}
                />
            )}

            {/* Récapitulatif des comptes */}
            <div style={{ border: `1px solid ${signupPalette.border}`, borderRadius: 16, padding: '8px 16px' }}>
                <List
                    size="small"
                    dataSource={[
                        {
                            key: 'parent',
                            title: result.parent.email || result.parent.login,
                            subtitle: t('signup.done.parentAccount', 'Votre compte parent'),
                            login: result.parent.login,
                        },
                        ...(result.spouse
                            ? [
                                  {
                                      key: 'spouse',
                                      title: result.spouse.email || result.spouse.login,
                                      subtitle: t('signup.done.spouseAccount', 'Compte conjoint — lien envoyé par email'),
                                      login: result.spouse.login,
                                  },
                              ]
                            : []),
                        ...result.children.map((child) => ({
                            key: `child-${child.id}`,
                            title: child.display_name,
                            subtitle: t('signup.done.childAccount', 'Compte enfant'),
                            login: child.login,
                        })),
                    ]}
                    renderItem={(item) => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<TeamOutlined style={{ fontSize: 18, color: signupPalette.primary }} />}
                                title={<Text style={{ fontWeight: 600 }}>{item.title}</Text>}
                                description={
                                    <Space size={8} wrap>
                                        <Text style={{ fontSize: 12, color: signupPalette.textSecondary }}>{item.subtitle}</Text>
                                        <Tag>{item.login}</Tag>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
            </div>

            <Text style={{ fontSize: 12, color: signupPalette.textSecondary, textAlign: 'center', display: 'block' }}>
                {t(
                    'signup.done.childrenHint',
                    'Les identifiants des enfants sont modifiables depuis « Mes utilisateurs » une fois connecté.'
                )}
            </Text>

            {sessionReady ? (
                <Button
                    type="primary"
                    size="large"
                    block
                    onClick={onEnterApp}
                    style={{ height: 52, borderRadius: 12, border: 'none', background: signupGradient, fontWeight: 600, fontSize: 16 }}
                >
                    {t('signup.done.enterApp', 'Accéder à mon espace')}
                </Button>
            ) : (
                <Button
                    size="large"
                    block
                    onClick={onGoToLogin}
                    style={{ height: 52, borderRadius: 12, fontWeight: 600, fontSize: 16 }}
                >
                    {t('signup.done.goToLogin', 'Aller à la page de connexion')}
                </Button>
            )}
        </Space>
    );
};

export default SignupDone;
